/**
 * PortfolioBlendCompareCard — Round 96e (Jul 2026).
 *
 * A permanent "Portfolio Blend vs …" comparison card that lives on
 * the Watchlist tab, right above the Pinned Comparisons row. Uses
 * the same visual grammar as `PinnedPairsRow` cards so the two feel
 * like one continuous shelf.
 *
 * Left slot = the user's synthetic Portfolio Blend
 * (`/api/portfolio/blend-snapshot`).  Right slot = any watchlist
 * or portfolio ticker the user picks from the dropdown. Both slots
 * render yield · price · dividend rate.
 *
 * High-utility for the user: "how does XYZ stack against MY
 * portfolio right now?" without opening the full CompareModal.
 * Self-hides when the user has no positions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { GitCompare, PieChart, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import useCpiYoy from '@/hooks/useCpiYoy';
import { computeRealYield } from '@/lib/realYield';
import InlinePopover from '@/components/InlinePopover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPct = (v) => {
  const n = Number(v);
  if (!isFinite(n) || v == null) return '—';
  return `${n.toFixed(2)}%`;
};
const fmt$ = (v) => {
  const n = Number(v);
  if (!isFinite(n) || v == null) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function PortfolioBlendCompareCard({ user, ownedTickers = [], watchlistTickers = [], onOpenStock }) {
  const [blend, setBlend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rightStock, setRightStock] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Round 96g (Jul 2026) — 3-month total-return comparison. Matches
  // the Portfolio page's Benchmark Comparison arrow/colour grammar
  // so users get an instant "is XYZ ahead of or behind my
  // portfolio right now?" read-out inside the same card.
  const [stockReturn3m, setStockReturn3m] = useState(null);
  const [portfolioReturn3m, setPortfolioReturn3m] = useState(null);
  // Round 96n (Jul 3 2026) — CPI YoY for a real-yield chip on both
  // slots. We ALWAYS show the real yield inline (no toggle needed on
  // this tiny card) so users can see at a glance whether the blend or
  // the compared ticker is actually beating inflation.
  const cpiYoy = useCpiYoy();

  const fetchBlend = useCallback(async () => {
    if (!user) { setBlend(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/portfolio/blend-snapshot`, { withCredentials: true });
      setBlend(data || null);
    } catch {
      setBlend(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchBlend(); }, [fetchBlend]);

  // Round 96i (Jul 2026) — dropdown pool is WATCHLIST-ONLY. The whole
  // point of the card on the Watchlist tab is "how does this stock
  // I'm watching stack against my full portfolio?" — including
  // owned tickers would mean comparing a stock against a blend it's
  // already part of, which is meaningless. Portfolio tickers are
  // excluded even if they appear in the watchlist array.
  const pool = useMemo(() => {
    const owned = new Set((ownedTickers || []).map((t) => String(t || '').toUpperCase()));
    const set = new Set();
    (watchlistTickers || []).forEach((t) => {
      const up = String(t || '').toUpperCase();
      if (up && !owned.has(up)) set.add(up);
    });
    return Array.from(set).sort();
  }, [watchlistTickers, ownedTickers]);

  // Auto-pick the first watchlist-only ticker so the card is never
  // empty on first render. If the selected ticker leaves the pool
  // (user un-stars it or buys it), reset to the first available.
  useEffect(() => {
    if (pool.length === 0) { setSelected(null); return; }
    if (!selected || !pool.includes(selected)) setSelected(pool[0]);
  }, [pool, selected]);

  useEffect(() => {
    if (!selected) { setRightStock(null); setStockReturn3m(null); return; }
    let cancelled = false;
    axios.get(`${API}/stocks/batch/${encodeURIComponent(selected)}`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = data?.stocks || data || [];
        const row = Array.isArray(rows) ? rows[0] : rows;
        setRightStock(row || null);
      })
      .catch(() => { if (!cancelled) setRightStock(null); });
    // 3M return for the selected ticker (DRIP-on).
    axios.get(`${API}/watchlist/time-machine/${encodeURIComponent(selected)}`, {
      params: { years: 0.25 },
      withCredentials: true,
    })
      .then(({ data }) => {
        if (cancelled) return;
        setStockReturn3m(data?.total_return_pct ?? null);
      })
      .catch(() => { if (!cancelled) setStockReturn3m(null); });
    return () => { cancelled = true; };
  }, [selected]);

  // Portfolio 3M return — fetched once on mount because it depends on
  // the whole portfolio, not on which ticker the user is comparing.
  useEffect(() => {
    if (!user) { setPortfolioReturn3m(null); return; }
    let cancelled = false;
    axios.get(`${API}/portfolio/benchmark`, {
      params: { period: '3m' },
      withCredentials: true,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const pf = (data?.benchmarks || data?.results || []).find((r) => r?.name === 'MY PORTFOLIO');
        setPortfolioReturn3m(pf?.return_pct ?? null);
      })
      .catch(() => { if (!cancelled) setPortfolioReturn3m(null); });
    return () => { cancelled = true; };
  }, [user]);

  if (loading || !user) return null;
  if (!blend || (blend.num_holdings ?? 0) === 0) return null;
  // Nothing meaningful to compare against — hide the card entirely.
  if (pool.length === 0) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 dark:bg-fuchsia-500/10 p-3"
      data-testid="portfolio-blend-compare-card"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex flex-col">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-1.5">
            <PieChart className="w-3.5 h-3.5" />
            Compare vs Portfolio Blend
          </h3>
          <p className="text-[10px] text-muted-foreground italic mt-0.5">
            Only shows watchlist stocks you don't already own
          </p>
        </div>
        <p className="text-[10px] text-muted-foreground italic hidden md:inline">
          Live snapshot of {blend.num_holdings} holding{blend.num_holdings === 1 ? '' : 's'} · weighted by market value
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Left slot — Portfolio Blend */}
        <div
          className="flex-1 min-w-[200px] rounded-md border border-fuchsia-500/40 bg-card p-2"
          data-testid="portfolio-blend-left-slot"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <PieChart className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />
            <span className="font-mono text-xs font-bold text-fuchsia-700 dark:text-fuchsia-300 truncate">
              Portfolio Blend
            </span>
            <span className="ml-auto text-[10px] text-muted-foreground">
              {blend.num_holdings} tkr
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1 text-[10px] font-mono">
            <div className="flex flex-col">
              <span className="text-muted-foreground">Yield</span>
              <span className="font-bold text-fuchsia-700 dark:text-fuchsia-300" data-testid="blend-nominal-yield">{fmtPct(blend.dividend_yield)}</span>
              {typeof cpiYoy === 'number' && blend.dividend_yield != null && (() => {
                const real = computeRealYield(Number(blend.dividend_yield), cpiYoy);
                if (real == null) return null;
                const cls = real < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
                return (
                  <span
                    className={`text-[9px] font-semibold ${cls}`}
                    title={`Real yield (Fisher-exact) = (1 + ${blend.dividend_yield.toFixed(2)}%) / (1 + CPI ${cpiYoy.toFixed(2)}%) − 1 = ${real.toFixed(2)}%. Blended across all your positions.`}
                    data-testid="blend-real-yield"
                  >
                    {real >= 0 ? '+' : ''}{real.toFixed(2)}% real
                  </span>
                );
              })()}
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Price</span>
              <span className="text-fuchsia-700/80 dark:text-fuchsia-300/80">{fmt$(blend.price)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Rate</span>
              <span className="text-fuchsia-700/80 dark:text-fuchsia-300/80">{fmt$(blend.dividend_rate)}</span>
            </div>
          </div>
        </div>

        <GitCompare className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />

        {/* Right slot — ticker picker */}
        <div
          className="flex-1 min-w-[200px] rounded-md border bg-card p-2 relative"
          data-testid="portfolio-blend-right-slot"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              className="inline-flex items-center gap-1 font-mono text-xs font-bold text-violet-600 dark:text-violet-400 hover:underline"
              data-testid="portfolio-blend-ticker-picker"
              title="Pick a ticker to compare"
            >
              {selected || 'Pick a stock'}
              <ChevronDown className="w-3 h-3" />
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => onOpenStock?.(selected)}
                className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline"
                data-testid="portfolio-blend-open-stock"
                title={`Open ${selected} details`}
              >
                Open
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1 text-[10px] font-mono">
            <div className="flex flex-col">
              <span className="text-muted-foreground">Yield</span>
              <span className="font-bold text-violet-700 dark:text-violet-300" data-testid="blend-right-nominal-yield">{fmtPct(rightStock?.dividend_yield)}</span>
              {typeof cpiYoy === 'number' && rightStock?.dividend_yield != null && (() => {
                const real = computeRealYield(Number(rightStock.dividend_yield), cpiYoy);
                if (real == null) return null;
                const cls = real < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
                return (
                  <span
                    className={`text-[9px] font-semibold ${cls}`}
                    title={`Real yield (Fisher-exact) = (1 + ${Number(rightStock.dividend_yield).toFixed(2)}%) / (1 + CPI ${cpiYoy.toFixed(2)}%) − 1 = ${real.toFixed(2)}%.`}
                    data-testid="blend-right-real-yield"
                  >
                    {real >= 0 ? '+' : ''}{real.toFixed(2)}% real
                  </span>
                );
              })()}
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Price</span>
              <span className="text-violet-700/80 dark:text-violet-300/80">{fmt$(rightStock?.price)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Rate</span>
              <span className="text-violet-700/80 dark:text-violet-300/80">{fmt$(rightStock?.dividend_rate)}</span>
            </div>
          </div>

          {dropdownOpen && (
            <div
              className="absolute left-0 right-0 top-full mt-1 z-30 max-h-64 overflow-y-auto rounded-md border bg-popover shadow-lg"
              data-testid="portfolio-blend-picker-menu"
            >
              {pool.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">No watchlist stocks to compare — star a stock you don't already own to see it here.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {pool.map((t) => (
                    <li key={t}>
                      <button
                        type="button"
                        onClick={() => { setSelected(t); setDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs font-mono hover:bg-muted transition-colors ${selected === t ? 'bg-muted/60 font-bold' : ''}`}
                        data-testid={`portfolio-blend-picker-option-${t}`}
                      >
                        {t}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Verdict line — quick "your blend is higher/lower yield" callout */}
      {rightStock?.dividend_yield != null && blend?.dividend_yield != null && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {(() => {
            const diff = Number(rightStock.dividend_yield) - Number(blend.dividend_yield);
            const abs = Math.abs(diff).toFixed(2);
            if (Math.abs(diff) < 0.05) return `${selected} yields roughly the same as your blend.`;
            return diff > 0
              ? <>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{selected}</span> yields <span className="font-mono font-bold">+{abs}%</span> higher than your Portfolio Blend.
                </>
              : <>
                  <span className="font-semibold text-rose-600 dark:text-rose-400">{selected}</span> yields <span className="font-mono font-bold">-{abs}%</span> lower than your Portfolio Blend.
                </>;
          })()}
        </p>
      )}

      {/* Round 96g (Jul 2026) — 3-month total-return delta bar.
          Renders below the two-slot row when we have both the
          selected ticker's 3M return and the portfolio's 3M return.
          Green up arrow = stock is ahead; rose down arrow = behind.
          Colours + arrows mirror the Portfolio page's Benchmark
          Comparison card so users read them the same way here. */}
      {stockReturn3m != null && portfolioReturn3m != null && selected && (
        (() => {
          const delta = Number(stockReturn3m) - Number(portfolioReturn3m);
          const pos = delta >= 0;
          const Arrow = pos ? TrendingUp : TrendingDown;
          const tip = `${selected} returned ${stockReturn3m >= 0 ? '+' : ''}${stockReturn3m.toFixed(2)}% over the last 3 months. Your portfolio returned ${portfolioReturn3m >= 0 ? '+' : ''}${portfolioReturn3m.toFixed(2)}% over the same window — ${selected} is ${pos ? 'ahead of' : 'behind'} your portfolio by ${Math.abs(delta).toFixed(2)}%.`;
          return (
            <div
              className="mt-3 rounded-md border border-fuchsia-300/40 dark:border-fuchsia-800/40 bg-card px-3 py-2 cursor-help"
              title={tip}
              data-testid="blend-vs-portfolio-3m"
            >
              <div className="flex flex-wrap items-center justify-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-600 dark:text-fuchsia-400">
                    vs My Portfolio
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">3M return</span>
                </div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {/* Round 105d (Jun 2026) — each box is a click
                      popover showing the same explainer the desktop
                      hover title carries, so phones get it too. */}
                  <InlinePopover
                    className="flex flex-col items-center rounded-md px-2 py-1 bg-amber-100 dark:bg-amber-950/40 ring-1 ring-amber-300/60 dark:ring-amber-700/50 cursor-help"
                    title={tip}
                    testId="blend-3m-box-portfolio"
                    maxWidth={280}
                    render={() => (
                      <p className="text-left text-[11px] leading-snug text-foreground/90">{tip}</p>
                    )}
                  >
                    <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-bold">Portfolio</span>
                    <span className={`text-xs font-mono font-bold ${portfolioReturn3m >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {portfolioReturn3m >= 0 ? '+' : ''}{portfolioReturn3m.toFixed(2)}%
                    </span>
                  </InlinePopover>
                  <InlinePopover
                    className="flex flex-col items-center rounded-md px-2 py-1 bg-amber-100 dark:bg-amber-950/40 ring-1 ring-amber-300/60 dark:ring-amber-700/50 cursor-help"
                    title={tip}
                    testId="blend-3m-box-ticker"
                    maxWidth={280}
                    render={() => (
                      <p className="text-left text-[11px] leading-snug text-foreground/90">{tip}</p>
                    )}
                  >
                    <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-bold">{selected}</span>
                    <span className={`text-xs font-mono font-bold ${stockReturn3m >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {stockReturn3m >= 0 ? '+' : ''}{stockReturn3m.toFixed(2)}%
                    </span>
                  </InlinePopover>
                  <InlinePopover
                    className="flex flex-col items-center rounded-md px-2 py-1 bg-amber-100 dark:bg-amber-950/40 ring-1 ring-amber-300/60 dark:ring-amber-700/50 cursor-help"
                    title={tip}
                    testId="blend-3m-box-return"
                    maxWidth={280}
                    render={() => (
                      <p className="text-left text-[11px] leading-snug text-foreground/90">{tip}</p>
                    )}
                  >
                    <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-bold">Return</span>
                    <span className={`inline-flex items-center gap-0.5 text-xs font-mono font-bold ${pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      <Arrow className="w-3.5 h-3.5" />
                      {pos ? '+' : ''}{delta.toFixed(2)}%
                    </span>
                  </InlinePopover>
                </div>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}
